import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  
  #isGiftCheckProcessing = false;

  get #mainHandle()       { return this.dataset.mainHandle || 'savemec'; }
  get #gift1Id()          { return this.dataset.gift1Id || ''; }
  get #gift1Threshold()   { return parseInt(this.dataset.gift1Threshold || '0') || 2; }
  get #gift2Id()          { return this.dataset.gift2Id || ''; }
  get #gift2Threshold()   { return parseInt(this.dataset.gift2Threshold || '0') || 3; }
  get #gift3Id()          { return this.dataset.gift3Id || ''; }
  get #gift3Threshold()   { return parseInt(this.dataset.gift3Threshold || '0') || 4; }
  get #doubleThreshold()  { return parseInt(this.dataset.giftDoubleThreshold || '0') || 6; }

  /** @returns {HTMLElement[]} */
  get #rows() {
    const r = this.refs.cartItemRows;
    return /** @type {HTMLElement[]} */ (Array.isArray(r) ? r : r ? [r] : []);
  }

  /** @returns {HTMLElement[]} */
  get #quantitySelectors() {
    const r = this.refs.quantitySelectors;
    return /** @type {HTMLElement[]} */ (Array.isArray(r) ? r : r ? [r] : []);
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /** @param {CustomEvent} event */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;
    const { quantity, cartLine: line } = event.detail;
    if (!line) return;

    if (quantity === 0) {
      this.onLineItemRemove(line);
    } else {
      if (this.#shouldShowGiftNotification(line, quantity)) {
        this.#showGiftToast();
      }
      this.updateQuantity({ line, quantity, action: 'change' });
      const lineItemRow = this.#rows.find(row => {
        const input = row.querySelector('input[data-cart-line]');
        return input && parseInt(input.getAttribute('data-cart-line') || '0') === line;
      });
      if (!lineItemRow) return;
      const textComponent = /** @type {any} */ (lineItemRow.querySelector('text-component'));
      textComponent?.shimmer();
    }
  }

  /**
   * @param {number} lineIndex
   * @param {number} newQty
   */
  #shouldShowGiftNotification(lineIndex, newQty) {
    const mainHandle = this.#mainHandle;
    const giftThresholds = [this.#gift1Threshold, this.#gift2Threshold, this.#gift3Threshold, this.#doubleThreshold];
    const rows = this.#rows;
    // Find by data-cart-line instead of DOM position — two-pass rendering reorders DOM
    // but data-cart-line always reflects the true Shopify cart line index
    const targetRow = rows.find(row => {
      const input = row.querySelector('input[data-cart-line]');
      return input && parseInt(input.getAttribute('data-cart-line') || '0') === lineIndex;
    });
    if (!targetRow) return false;

    const handle = targetRow.dataset.productHandle || '';
    if (!handle.includes(mainHandle)) return false;

    const oldQty = parseInt(targetRow.querySelector('input')?.getAttribute('data-cart-quantity') || '0');
    if (newQty <= oldQty) return false;

    let totalOld = 0;
    for (const row of rows) {
      if ((row.dataset.productHandle || '').includes(mainHandle)) {
        totalOld += parseInt(row.querySelector('input')?.getAttribute('data-cart-quantity') || '0');
      }
    }
    const totalNew = totalOld - oldQty + newQty;

    return giftThresholds.some(t => totalOld < t && totalNew >= t);
  }

  #showGiftToast() {
    const scrollHint = this.querySelector('scroll-hint.cart-drawer__items');
    const fallback = this.querySelector('.cart-drawer__summary');
    if (!scrollHint && !fallback) return;
    let toast = /** @type {HTMLElement|null} */ (this.querySelector('#cart-gift-toast'));
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'cart-gift-toast';
      toast.innerHTML = `<span class="cart-gift-toast__icon">🎁</span><span class="cart-gift-toast__text">Your free gift is being added<span class="cart-gift-toast__dots"><span></span><span></span><span></span></span></span>`;
      if (scrollHint) {
        scrollHint.insertAdjacentElement('beforeend', toast);
      } else {
        fallback.insertAdjacentElement('beforebegin', toast);
      }
    }
    toast.style.display = 'flex';
    requestAnimationFrame(() => requestAnimationFrame(() => /** @type {HTMLElement} */ (toast).classList.add('visible')));
  }

  #hideGiftToast() {
    const toast = /** @type {HTMLElement|null} */ (this.querySelector('#cart-gift-toast'));
    if (!toast) return;
    toast.classList.remove('visible');
    toast.addEventListener('transitionend', () => { /** @type {HTMLElement} */ (toast).style.display = 'none'; }, { once: true });
  }

  /** @param {number} line */
  onLineItemRemove(line) {
    this.updateQuantity({ line, quantity: 0, action: 'clear' });
    const cartItemRowToRemove = this.#rows.find(row => {
      const input = row.querySelector('input[data-cart-line]');
      return input && parseInt(input.getAttribute('data-cart-line') || '0') === line;
    });
    if (!cartItemRowToRemove) return;
    const rowsToRemove = [cartItemRowToRemove, ...this.#rows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key)];
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();
      if (prefersReducedMotion()) return remove();
      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Main quantity update function, now async to handle gift logic sequentially.
   * @param {{line: number, quantity: number, action: string}} config
   */
  async updateQuantity(config) {
    const cartPerformanceMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    this.#disableCartItems();
    const cartTotal = /** @type {any} */ (this.refs.cartTotal);

    try {
      // Step 1: Update the primary item's quantity
      const sectionsToUpdate = new Set([this.sectionId, ...Array.from(document.querySelectorAll('cart-items-component')).map(el => /** @type {HTMLElement} */ (el).dataset.sectionId).filter(Boolean)]);
      const body = JSON.stringify({
        line: config.line,
        quantity: config.quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();
      
      const initialResponse = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      let cartAfterPrimaryUpdate = await initialResponse.json();

      if (cartAfterPrimaryUpdate.errors) {
        this.#handleCartError(config.line, cartAfterPrimaryUpdate);
        return; // Stop processing if the initial update failed
      }
      
      // Step 2: Check for and apply gift updates based on the new cart state
      const cartAfterGiftUpdate = await this.#checkAndManageGifts(cartAfterPrimaryUpdate);
      const finalCartState = cartAfterGiftUpdate || cartAfterPrimaryUpdate; // Use the result from gift logic if it ran

      // Step 3: Render the final state of the cart
      resetShimmer(this);

      // We use the final state to update the UI
      this.#updateQuantitySelectors(finalCartState);

      this.dispatchEvent(
        new CartUpdateEvent(finalCartState, this.sectionId, {
          itemCount: finalCartState.item_count,
          source: 'cart-items-component',
          sections: finalCartState.sections,
        })
      );

      // Morph the section using the final cart state's HTML
      if (finalCartState.sections && finalCartState.sections[this.sectionId]) {
        morphSection(this.sectionId, finalCartState.sections[this.sectionId]);
      }

      this.#hideGiftToast();
      this.#updateCartQuantitySelectorButtonStates();

    } catch (error) {
      console.error('CartItems updateQuantity Error:', error);
      this.#hideGiftToast();
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    } finally {
      this.#enableCartItems();
      cartPerformance.measureFromMarker(cartPerformanceMarker);
    }
  }

  /**
   * Integrated gift management logic.
   * @param {any} cartData - The current state of the cart.
   * @returns {Promise<any|null>} The final cart state after gift updates, or null if no changes were made.
   */
  async #checkAndManageGifts(cartData) {
    if (this.#isGiftCheckProcessing || !cartData || !Array.isArray(cartData.items)) {
      return null;
    }
    this.#isGiftCheckProcessing = true;

    try {
      const mainHandle = this.#mainHandle;
      let saveMecQuantity = 0;

      for (const item of cartData.items) {
        const productHandle = item.product_handle || item.handle || '';
        if (productHandle.includes(mainHandle) || item.product_title?.toLowerCase().includes(mainHandle)) {
          saveMecQuantity += item.quantity;
        }
      }
      
      const dbl = this.#doubleThreshold;
      const gifts = [
        { id: this.#gift1Id, threshold: this.#gift1Threshold },
        { id: this.#gift2Id, threshold: this.#gift2Threshold },
        { id: this.#gift3Id, threshold: this.#gift3Threshold },
      ];

      /** @type {Record<string, number>} */
      const updates = {};

      for (const gift of gifts) {
        if (!gift.id) continue;
        const target = saveMecQuantity >= dbl ? 2 : saveMecQuantity >= gift.threshold ? 1 : 0;
        const allInstances = cartData.items.filter((/** @type {any} */ i) => String(i.variant_id) === gift.id);
        if (allInstances.length === 0) {
          if (target > 0) updates[gift.id] = target;
        } else {
          if (allInstances[0].quantity !== target) updates[allInstances[0].key] = target;
          // Remove duplicate gift line items
          for (let j = 1; j < allInstances.length; j++) updates[allInstances[j].key] = 0;
        }
      }

      if (Object.keys(updates).length > 0) {
        const sectionsToUpdate = new Set([this.sectionId, ...Array.from(document.querySelectorAll('cart-items-component')).map(el => /** @type {HTMLElement} */ (el).dataset.sectionId).filter(Boolean)]);
        const body = {
          updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname
        };

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (res.ok) {
          return await res.json(); // Return the final, updated cart state
        }
      }
    } catch (error) {
      console.error('Gift management check failed:', error);
    } finally {
      this.#isGiftCheckProcessing = false;
    }
    return null; // No changes made or an error occurred
  }

  handleDiscountUpdate = (/** @type {any} */ event) => this.#handleCartUpdate(event);

  #handleCartError = (/** @type {number} */ line, /** @type {any} */ parsedResponseText) => {
    const quantitySelector = this.#quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');
    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }
    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];
    if (cartItemError instanceof HTMLElement && cartItemErrorContainer instanceof HTMLElement) {
      cartItemError.textContent = parsedResponseText.errors;
      cartItemErrorContainer.classList.remove('hidden');
    }
  };

  #handleCartUpdate = (/** @type {any} */ event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  #disableCartItems = () => this.classList.add('cart-items-disabled');
  #enableCartItems = () => this.classList.remove('cart-items-disabled');

  /** @param {any} updatedCart */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;
    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);
      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;
        input.setAttribute('data-cart-quantity', item.quantity.toString());
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      (/** @type {any} */ (selector)).updateButtonStates?.();
    }
  }

  get sectionId() {
    const { sectionId } = this.dataset;
    if (!sectionId) throw new Error('Section id missing');
    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
